import React from 'react';
import {
  Typography,
  Container,
  Box,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  useTheme,
  keyframes,
  styled,
  Chip
} from '@mui/material';
import { Link } from 'react-router-dom';
import { ArrowBack, Security, DataObject, ContactMail } from '@mui/icons-material';

// Gradient animation for title
const gradient = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
`;

const AnimatedTitle = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.info.main})`,
  backgroundSize: '200% 200%',
  animation: `${gradient} 4s ease infinite`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 700,
}));

const DatenschutzPage: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
        py: 8
      }}
    >
      {/* Animated Background Layers */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 20%, ${theme.palette.primary.main}12 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, ${theme.palette.secondary.main}10 0%, transparent 50%),
            radial-gradient(circle at 60% 40%, ${theme.palette.info.main}08 0%, transparent 50%)
          `,
          animation: `${pulse} 8s ease-in-out infinite`,
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ mb: 4 }}>
          <Link
            to="/"
            style={{
              color: theme.palette.primary.main,
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              marginBottom: '1rem'
            }}
          >
            <ArrowBack sx={{ mr: 1 }} />
            Zurück zur Startseite
          </Link>

          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip
              label="🔒 Datenschutz"
              color="primary"
              variant="outlined"
              sx={{
                mb: 3,
                fontSize: '1rem',
                py: 1,
                px: 3,
                fontWeight: 600,
                backdropFilter: 'blur(10px)',
                background: `${theme.palette.primary.main}15`,
                border: `2px solid ${theme.palette.primary.main}50`,
              }}
            />
            <AnimatedTitle variant="h2" gutterBottom>
              Datenschutzerklärung
            </AnimatedTitle>
            <Typography variant="h6" color="text.secondary" sx={{ opacity: 0.9 }}>
              Zuletzt aktualisiert: {new Date().toLocaleDateString('de-DE')}
            </Typography>
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.background.paper}60 0%, ${theme.palette.primary.main}08 100%)`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `1px solid ${theme.palette.primary.main}30`,
            boxShadow: `0 8px 32px ${theme.palette.primary.main}20`,
          }}
        >
          <Typography variant="h4" gutterBottom color="primary" fontWeight={700}>
            1. Verantwortlicher
          </Typography>
          <Typography paragraph>
            Verantwortlich für die Datenverarbeitung auf dieser Website ist:
          </Typography>
          <Box sx={{
            p: 3,
            border: `2px solid ${theme.palette.primary.main}30`,
            borderRadius: 2,
            background: `${theme.palette.primary.main}10`
          }}>
            <Typography><strong>Smart Storage Checker</strong></Typography>
            <Typography>Ein Service der InnCharge GmbH</Typography>
            <Typography>[Straße und Hausnummer]</Typography>
            <Typography>[PLZ] [Ort]</Typography>
            <Typography>[Land]</Typography>
            <Typography sx={{ mt: 1 }}>
              E-Mail: [Datenschutz E-Mail]<br />
              Telefon: [Telefonnummer]
            </Typography>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.background.paper}60 0%, ${theme.palette.secondary.main}08 100%)`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `1px solid ${theme.palette.secondary.main}30`,
            boxShadow: `0 8px 32px ${theme.palette.secondary.main}20`,
          }}
        >
          <Typography variant="h4" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}>
            2. Erhebung und Verarbeitung personenbezogener Daten
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
            2.1 CSV-Upload und Analyse
          </Typography>
          <Typography paragraph>
            Beim Upload Ihrer CSV-Dateien zur Energiespeicher-Analyse verarbeiten wir folgende Daten:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Leistungsprofil-Daten"
                secondary="Zeitstempel, Leistungswerte, Energiedaten aus Ihrer CSV-Datei"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Upload-Metadaten"
                secondary="Dateiname, Dateigröße, Upload-Zeitpunkt"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Technische Daten"
                secondary="IP-Adresse, Browser-Informationen, Session-ID"
              />
            </ListItem>
          </List>

          <Typography variant="h5" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
            2.2 Lead-Generierung und Kontaktformular
          </Typography>
          <Typography paragraph>
            Wenn Sie unser Kontaktformular nutzen, verarbeiten wir:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Kontaktdaten"
                secondary="Name, E-Mail-Adresse, Unternehmen, Telefonnummer"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Nachrichteninhalt"
                secondary="Ihre Anfrage, Kommentare, spezielle Anforderungen"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Analyseergebnisse"
                secondary="Verknüpfung zu Ihren Upload-Analysen für bessere Beratung"
              />
            </ListItem>
          </List>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.background.paper}60 0%, ${theme.palette.info.main}08 100%)`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `1px solid ${theme.palette.info.main}30`,
            boxShadow: `0 8px 32px ${theme.palette.info.main}20`,
          }}
        >
          <Typography variant="h4" gutterBottom color="primary" fontWeight={700}>
            3. Rechtsgrundlagen der Verarbeitung
          </Typography>
          <Typography paragraph>
            Die Verarbeitung Ihrer personenbezogenen Daten erfolgt auf Grundlage der DSGVO:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="Art. 6 Abs. 1 lit. b DSGVO"
                secondary="Vertragserfüllung - Bereitstellung unserer Analyse-Services"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Art. 6 Abs. 1 lit. f DSGVO"
                secondary="Berechtigtes Interesse - Verbesserung unserer Services und Lead-Generierung"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Art. 6 Abs. 1 lit. a DSGVO"
                secondary="Einwilligung - bei freiwilligen Angaben im Kontaktformular"
              />
            </ListItem>
          </List>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.background.paper}60 0%, ${theme.palette.success.main}08 100%)`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `1px solid ${theme.palette.success.main}30`,
            boxShadow: `0 8px 32px ${theme.palette.success.main}20`,
          }}
        >
          <Typography variant="h4" gutterBottom color="primary" fontWeight={700}>
            4. Speicherdauer und Löschung
          </Typography>
          <Typography paragraph>
            Wir speichern Ihre Daten nur so lange, wie es für die Zweckerfüllung erforderlich ist.
            Die Speicherdauer richtet sich nach den gesetzlichen Aufbewahrungspflichten und dem
            Geschäftszweck:
          </Typography>

          <Box sx={{
            p: 3,
            borderRadius: 2,
            backgroundColor: theme.palette.info.main + '10',
            border: `1px solid ${theme.palette.info.main}30`,
            mb: 3
          }}>
            <Typography variant="body1" fontWeight="600" sx={{ mb: 1 }}>
              Rechtliche Grundlage für 7-jährige Speicherung:
            </Typography>
            <Typography variant="body2">
              • <strong>Steuerrechtliche Aufbewahrung:</strong> Geschäftsrelevante Unterlagen (je nach Firmensitz)<br/>
              • <strong>Unternehmensrechtliche Pflichten:</strong> Aufbewahrung von Geschäftsbriefen<br/>
              • <strong>DSGVO Art. 17 Abs. 3 lit. b:</strong> Ausnahme vom Löschungsrecht bei rechtlichen Verpflichtungen<br/>
              • <strong>Beweissicherung:</strong> Schutz vor Haftungsansprüchen und Gewährleistung
            </Typography>
          </Box>
          <List>
            <ListItem>
              <ListItemText
                primary="CSV-Analysedaten"
                secondary="7 Tage nach Upload (automatische Löschung)"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Kontakt-/Lead-Daten"
                secondary="7 Jahre (gesetzliche Aufbewahrungspflicht je nach Firmensitz)"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Consent-Protokolle"
                secondary="3 Jahre (DSGVO-Compliance Nachweis)"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Technische Logs"
                secondary="7 Tage (zur Fehlerdiagnose und Sicherheit)"
              />
            </ListItem>
          </List>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.background.paper}60 0%, ${theme.palette.warning.main}08 100%)`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `1px solid ${theme.palette.warning.main}30`,
            boxShadow: `0 8px 32px ${theme.palette.warning.main}20`,
          }}
        >
          <Typography variant="h4" gutterBottom color="primary" fontWeight={700}>
            5. Ihre Rechte
          </Typography>
          <Typography paragraph>
            Als betroffene Person haben Sie folgende Rechte:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="Auskunftsrecht (Art. 15 DSGVO)"
                secondary="Information über die zu Ihrer Person gespeicherten Daten"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Berichtigungsrecht (Art. 16 DSGVO)"
                secondary="Korrektur unrichtiger personenbezogener Daten"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Löschungsrecht (Art. 17 DSGVO)"
                secondary="Löschen Ihrer personenbezogenen Daten"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Einschränkungsrecht (Art. 18 DSGVO)"
                secondary="Einschränkung der Verarbeitung Ihrer Daten"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Datenübertragbarkeit (Art. 20 DSGVO)"
                secondary="Erhalt Ihrer Daten in strukturiertem, maschinenlesbarem Format"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Widerspruchsrecht (Art. 21 DSGVO)"
                secondary="Widerspruch gegen die Verarbeitung Ihrer Daten"
              />
            </ListItem>
          </List>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.background.paper}60 0%, ${theme.palette.error.main}08 100%)`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `1px solid ${theme.palette.error.main}30`,
            boxShadow: `0 8px 32px ${theme.palette.error.main}20`,
          }}
        >
          <Typography variant="h4" gutterBottom color="primary" fontWeight={700}>
            6. Datensicherheit
          </Typography>
          <Typography paragraph>
            Wir treffen angemessene technische und organisatorische Maßnahmen zum Schutz Ihrer Daten:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="Verschlüsselung"
                secondary="HTTPS/TLS-Verschlüsselung für alle Datenübertragungen"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Zugriffskontrolle"
                secondary="Strenge Zugriffsbeschränkungen auf personenbezogene Daten"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Anonymisierung"
                secondary="Automatische Anonymisierung von Analysedaten nach 7 Tagen"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Sichere Server"
                secondary="Hosting in europäischen Rechenzentren mit hohen Sicherheitsstandards"
              />
            </ListItem>
          </List>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `2px solid ${theme.palette.primary.main}30`,
            boxShadow: `0 8px 32px ${theme.palette.primary.main}25`,
          }}
        >
          <Typography variant="h4" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}>
            <ContactMail sx={{ mr: 2 }} />
            7. Kontakt
          </Typography>
          <Typography paragraph>
            Bei Fragen zum Datenschutz oder zur Ausübung Ihrer Rechte kontaktieren Sie uns:
          </Typography>
          <Typography>
            <strong>E-Mail:</strong> [Datenschutz E-Mail]<br />
            <strong>Telefon:</strong> [Telefonnummer]<br />
            <strong>Post:</strong> InnCharge GmbH, Datenschutzbeauftragter, [Vollständige Adresse]
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h5" gutterBottom fontWeight={600}>
            Beschwerderecht
          </Typography>
          <Typography>
            Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde über die Verarbeitung
            Ihrer personenbezogenen Daten zu beschweren. Die zuständige Aufsichtsbehörde richtet sich
            nach dem Sitz des Unternehmens und wird nach Firmengründung entsprechend aktualisiert.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default DatenschutzPage;
